import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { getSelectedNoteIds, getActiveNoteId } from 'data/ui/selectors'
import { resetSelectedNoteIds } from 'data/ui/actions'
import { archiveNotes, unarchiveNotes, deleteNotes } from 'data/notes/actions'
import { getNote } from 'data/notes/selectors'
import { Text } from 'components/base'
import { ArrowRightIcon, DeleteIcon, UndoIcon } from 'components/Utils/Icons'
import { setActiveModal } from 'data/ui/actions'
import { MOVE_TO } from 'components/Utils/Modal/constants'

import BulkControlItem from './ControlItem'

class NotesBulkControl extends PureComponent {
  static propTypes = {
    // Data
    activeNoteId: PropTypes.string,
    selectedNoteIds: PropTypes.object.isRequired,
    isArchived: PropTypes.bool.isRequired,
    // Actions
    archiveNotes: PropTypes.func.isRequired,
    unarchiveNotes: PropTypes.func.isRequired,
    deleteNotes: PropTypes.func.isRequired,
    resetSelectedNoteIds: PropTypes.func.isRequired,
    setActiveModal: PropTypes.func.isRequired,
  }

  static defaultProps = {
    isArchived: false,
    activeNoteId: null,
  }

  state = {
    items: [],
    noteIds: [],
  }

  static getDerivedStateFromProps(props) {
    return {
      noteIds: NotesBulkControl.getSelectedNoteIds(props),
      items: NotesBulkControl.getControlItems(props),
    }
  }

  static getSelectedNoteIds = props => {
    return props.selectedNoteIds.toArray()
  }

  static getControlItems = props => {
    const {
      isArchived,
      setActiveModal,
      archiveNotes,
      deleteNotes,
      unarchiveNotes,
    } = props

    const items = [
      {
        id: 'move-to',
        icon: ArrowRightIcon,
        title: 'Move to',
        isAllowed: !isArchived,
        action: ({ noteIds }) => {
          setActiveModal({
            id: MOVE_TO,
            meta: { noteIds },
          })
        },
      },
      {
        id: 'archive',
        icon: DeleteIcon,
        title: 'Send to Trash',
        isAllowed: !isArchived,
        action: ({ noteIds }) => {
          archiveNotes({ noteIds })
        },
      },
      {
        id: 'restore',
        icon: UndoIcon,
        title: 'Restore to channel',
        isAllowed: isArchived,
        action: ({ noteIds }) => {
          unarchiveNotes({ noteIds })
        },
      },
      {
        id: 'delete',
        icon: DeleteIcon,
        title: 'Delete',
        isAllowed: isArchived,
        confirm: ({ noteIds }) => (
          <div>
            <div className="bold font-size-large center-text margin-bottom-large">
              Are you sure?
            </div>
            You are about to delete {noteIds.length} notes forever. This action
            is irrevocable!
          </div>
        ),
        action: ({ noteIds }) => {
          deleteNotes({ noteIds })
        },
      },
    ]
    return items.filter(item => item.isAllowed)
  }

  onClickAction = item => {
    item.action({
      noteIds: this.state.noteIds,
    })
  }

  onClickDismiss = () => {
    this.props.resetSelectedNoteIds()
  }

  renderControlItem = item => {
    return (
      <BulkControlItem
        key={item.id}
        item={item}
        noteIds={this.state.noteIds}
        onAction={this.onClickAction}
      />
    )
  }

  renderBulkControls() {
    return (
      <div
        style={{ minWidth: 480 }}
        className="flexbox flexbox-column flexbox-grow flexbox-align-center flexbox-centered"
      >
        <Text.Title className="margin-bottom-small">
          {`${this.state.noteIds.length} items selected`}
        </Text.Title>
        <Text.Action
          onClick={this.onClickDismiss}
          className="pointer margin-bottom-large"
        >
          Cancel selection
        </Text.Action>
        <div className="flexbox">
          {this.state.items.map(this.renderControlItem)}
        </div>
      </div>
    )
  }

  render() {
    return this.props.selectedNoteIds.size === 0
      ? this.props.children
      : this.renderBulkControls()
  }
}

function mapStateToProps(state) {
  const activeNoteId = getActiveNoteId(state)
  const selectedNoteIds = getSelectedNoteIds(state)

  if (selectedNoteIds.size === 0) {
    return {
      ...NotesBulkControl.defaultProps,
      activeNoteId,
      selectedNoteIds,
    }
  }

  const selectedFirstNote = getNote(state, selectedNoteIds.first())
  const isArchived = selectedFirstNote && Boolean(selectedFirstNote.archivedAt)

  return {
    activeNoteId,
    selectedNoteIds,
    isArchived,
  }
}

const mapDispatchToProps = {
  archiveNotes,
  unarchiveNotes,
  deleteNotes,
  setActiveModal,
  resetSelectedNoteIds,
}

const enhance = connect(
  mapStateToProps,
  mapDispatchToProps
)

export default enhance(NotesBulkControl)
